// AttachedDesignBanner — shown on Home when the customer has one or more
// attached designs (added by Kimi via /admin/studio/attach) that they
// haven't viewed yet. Click → opens the design's detail panel (or, for
// multiple, navigates to the From Market Dwellings tab in My Designs).
// Dismiss → marks every unviewed attached design as viewed so the banner
// stays gone.
//
// Renders nothing when there are no unviewed attached designs.

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, X } from 'lucide-react';
import { listDesigns, markAttachedViewed } from '../lib/api';
import { qk } from '../lib/queryClient';
import { useStudio } from '../lib/store';

export function AttachedDesignBanner() {
  const qc = useQueryClient();
  const setView = useStudio((s) => s.setView);
  const openDetail = useStudio((s) => s.openDetail);

  // Lightweight check — pull up to 3 unviewed attached designs to drive
  // the banner copy. listDesigns lets us filter source_kind=attached.
  // viewed_at filtering happens client-side since the backend doesn't
  // expose a viewed=false param yet (and one query is cheap).
  const query = useQuery({
    queryKey: qk.designs({ banner: 'attached-unviewed' }),
    queryFn: () => listDesigns({ source_kind: 'attached', limit: 12 }),
    staleTime: 30_000,
  });

  const unviewed = useMemo(
    () => (query.data?.items ?? []).filter((d) => !d.viewed_at),
    [query.data],
  );

  const dismissMut = useMutation({
    mutationFn: () => markAttachedViewed(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.designs() });
    },
  });

  if (query.isLoading) return null;
  if (unviewed.length === 0) return null;

  const featured = unviewed[0];
  const more = unviewed.length - 1;

  const onView = () => {
    if (unviewed.length === 1 && featured) {
      // Single new design — open its detail panel directly. Detail panel
      // hooks already stamp viewed_at; the banner will hide on next render.
      openDetail(featured.id);
    } else {
      // Multiple — drop them into the dedicated tab so the customer can
      // browse. The tab navigates them; mark-attached-viewed clears all.
      setView('designs');
      dismissMut.mutate();
    }
  };

  const onDismiss = () => {
    // X click — explicit dismissal. Mark everything as viewed.
    dismissMut.mutate();
  };

  return (
    <div className="attached-banner" role="region" aria-label="New custom design">
      <div className="attached-banner__left">
        <span className="attached-banner__icon" aria-hidden="true">
          <Sparkles size={20} strokeWidth={1.6} />
        </span>
        <div>
          <h4>New custom design from Market Dwellings</h4>
          <p>
            {featured?.name ?? 'A design'} {more > 0 ? `(and ${more} more)` : ''} is ready in your library.
          </p>
        </div>
      </div>
      <div className="attached-banner__actions">
        <button type="button" className="attached-banner__cta" onClick={onView}>
          {unviewed.length === 1 ? 'View' : 'See all'}
        </button>
        <button
          type="button"
          className="attached-banner__close"
          aria-label="Dismiss"
          onClick={onDismiss}
        >
          <X size={16} strokeWidth={1.8} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
